import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  PwmDocumentListCreateDocument,
  PwmDocumentListDeleteDocument,
  PwmDocumentListGetByIdDocument,
  PwmDocumentListPaginatedDocument,
  PwmDocumentListListDocument,
  PwmDocumentListUpdateDocument,
} from "api/generated";
import { Optional } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";
import {
  DocumentListByIdResponse,
  ICreateDocumentListArgs,
  IDocumentListArgs,
  IDocumentListModel,
  IDocumentListResponse,
  IDocumentListUpdateArgs,
} from "api";

import { PwmDocumentListModel } from "./types";

export async function getPwmDocumentListPaginated(
  variables: IDocumentListArgs
): Promise<IDocumentListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmDocumentListPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.pwmDocumentListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createPwmDocumentList(
  variables: ICreateDocumentListArgs
): Promise<Optional<Pick<IDocumentListModel, "documentListId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmDocumentListCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict all document list queries
    executeEviction(client, {
      typename: "PwmDocumentList",
      fieldNames: ["pwmDocumentListPaginated", "pwmDocumentListList"],
    });

    return res?.data?.pwmDocumentListCreate;
  } catch (error) {
    throw error;
  }
}

export async function getPwmDocumentList(
  documentListId: number
): Promise<DocumentListByIdResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmDocumentListGetByIdDocument,
      variables: {
        documentListId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmDocumentListGetById;
  } catch (error) {
    throw error;
  }
}

export async function updatePwmDocumentList(
  variables: IDocumentListUpdateArgs
): Promise<Optional<Pick<IDocumentListModel, "documentListId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmDocumentListUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + all lists + getById
    executeEviction(client, {
      typename: "PwmDocumentList",
      id: variables.documentListId.toString(),
      fieldNames: [
        "pwmDocumentListPaginated",
        "pwmDocumentListList",
        "pwmDocumentListGetById",
      ],
    });

    return res?.data?.pwmDocumentListUpdate;
  } catch (error) {
    throw error;
  }
}

export async function deletePwmDocumentList(
  documentListId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmDocumentListDeleteDocument,
      variables: {
        documentListId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict deleted entity + all lists
    executeEviction(client, {
      typename: "PwmDocumentList",
      id: documentListId.toString(),
      fieldNames: ["pwmDocumentListPaginated", "pwmDocumentListList"],
    });

    return Boolean(res?.data?.pwmDocumentListDelete);
  } catch (error) {
    throw error;
  }
}

export async function getPwmDocumentLists(): Promise<PwmDocumentListModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmDocumentListListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.pwmDocumentListList ?? [];
  } catch (error) {
    throw error;
  }
}
